import { useQuery } from '@tanstack/react-query';
import { Link } from 'wouter';
import { api } from '../lib/api';
import { money } from '../lib/format';
import type { ActionCenter as ActionCenterData, ActionItem } from '../types';
import { EmptyState, ErrorNotice } from './ui';

/** Сколько дел показываем сразу: остальное — за ссылкой в разделы. */
const VISIBLE_ACTIONS = 3;

/**
 * Что сделать сейчас. Список считает сервер по текущему состоянию заказов,
 * поэтому закрытое дело исчезает само — в отличие от уведомлений, которые
 * остаются в чате навсегда и не отвечают на вопрос «а что ещё висит».
 */
export function ActionCenter() {
  const query = useQuery({
    queryKey: ['actions'],
    queryFn: () => api<ActionCenterData>('/artist/actions'),
  });

  const items = query.data?.items ?? [];
  const visible = items.slice(0, VISIBLE_ACTIONS);

  return (
    <>
      <div className="section-heading dashboard-attention-heading">
        <h2>Что сделать сейчас</h2>
        {items.length > 0 && <span className="section-count">{items.length}</span>}
      </div>

      {query.error && <ErrorNotice error={query.error} />}

      {query.data && items.length === 0 && (
        <EmptyState
          title="Всё разобрано"
          text="Ни одной заявки без ответа и ни одного заказа без движения. Можно спокойно рисовать."
        />
      )}

      {visible.length > 0 && (
        <>
          <div className="attention-list">
            {visible.map((item) => (
              <Link
                key={`${item.target}-${item.id}-${item.kind}`}
                href={item.target === 'application' ? '/applications' : `/orders/${item.id}`}
                className={`attention-row attention-row--${toneOf(item)}`}
              >
                <span className="attention-row__marker">{markerOf(item)}</span>
                <span className="attention-row__content">
                  <strong>{actionText(item)}</strong>
                  <small>
                    {item.title} · {item.clientName} · {waitedFor(item.since)}
                  </small>
                </span>
                <span className="chevron" aria-hidden="true">
                  ›
                </span>
              </Link>
            ))}
          </div>
          {items.length > visible.length && (
            <div className="attention-more">
              <span>Ещё {items.length - visible.length}</span>
              <Link href="/applications">Заявки</Link>
              <Link href="/orders">Заказы</Link>
            </div>
          )}
        </>
      )}
    </>
  );
}

/** Каждое дело говорит, что нужно сделать, а не как оно называется в базе. */
function actionText(item: ActionItem): string {
  switch (item.kind) {
    case 'APPLICATION_NEW':
      return 'Ответьте на заявку';
    case 'CUSTOMER_MESSAGE':
      return 'Заказчик написал и ждёт ответа';
    case 'ORDER_OVERDUE':
      return 'Срок прошёл — предупредите заказчика';
    case 'ORDER_DEADLINE_SOON':
      return 'Срок совсем скоро';
    case 'ORDER_UNPAID':
      return item.amount
        ? `Предоплата ещё не пришла: ${money(item.amount, item.currency ?? 'RUB')}`
        : 'Предоплата ещё не пришла';
    case 'ORDER_STALLED':
      return 'Заказ давно не двигался';
    case 'ORDER_WAITS_CLIENT':
      return 'Ход за заказчиком — можно напомнить';
  }
}

function markerOf(item: ActionItem): string {
  switch (item.kind) {
    case 'APPLICATION_NEW':
      return 'NEW';
    case 'CUSTOMER_MESSAGE':
      return '✉';
    case 'ORDER_OVERDUE':
      return '!';
    case 'ORDER_DEADLINE_SOON':
      return '⏳';
    case 'ORDER_UNPAID':
      return '%';
    case 'ORDER_STALLED':
      return '⏸';
    case 'ORDER_WAITS_CLIENT':
      return '↗';
  }
}

function toneOf(item: ActionItem): string {
  if (item.severity === 'urgent') return 'danger';
  if (item.severity === 'waiting') return 'neutral';
  return item.kind === 'APPLICATION_NEW' ? 'accent' : 'warning';
}

/** Сколько дело уже ждёт: «сегодня» честнее, чем «0 дней». */
function waitedFor(since: string): string {
  const days = Math.floor((Date.now() - new Date(since).getTime()) / (24 * 60 * 60 * 1000));

  if (days <= 0) return 'сегодня';
  if (days === 1) return 'вчера';
  return `${days} дн. назад`;
}
